import React, { useEffect, useState } from "react";
import {
  invalidateFileUrl,
  resolveFileUrl,
} from "../../utils/SignedFileUrl";

type TICImageProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  imageUrl?: string;
  fallbackSrc?: string;
  loadingClassName?: string;
  onClose?: () => void;
  fillColor?: string;
  isThumbnail?: boolean;
  containerClass?: string;
  noLoader?: boolean;
};

const addThumb = (url: string | undefined): string | undefined => {
  if (url) {
    return url.replace(/\/([^/]+)$/, "/thumb-$1");
  }
};

const ICImage: React.FC<TICImageProps> = ({
  imageUrl,
  fallbackSrc,
  className,
  loadingClassName,
  src,
  alt,
  onClose,
  fillColor,
  onLoad,
  isThumbnail = false,
  noLoader = false,
  containerClass,
  ...rest
}) => {
  const [imageSrcUrl, setImageSrcUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [didFallbackToFull, setDidFallbackToFull] = useState(false);

  const trimmedImageUrl = imageUrl?.trim() ? imageUrl.trim() : undefined;
  const resolvedImageUrl = isThumbnail
    ? addThumb(trimmedImageUrl)
    : trimmedImageUrl;

  const handleImageLoad = (e: any) => {
    if (onLoad) {
      onLoad(e);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchImage = async () => {
      if (!resolvedImageUrl) {
        setImageSrcUrl(undefined);
        return;
      }
      setDidFallbackToFull(false);
      const signed = await resolveFileUrl(resolvedImageUrl);
      if (!cancelled) setImageSrcUrl(signed ?? undefined);
    };

    fetchImage();
    return () => {
      cancelled = true;
    };
  }, [resolvedImageUrl]);

  const handleImageError = async () => {
    if (!didFallbackToFull && resolvedImageUrl) {
      setDidFallbackToFull(true);
      invalidateFileUrl(resolvedImageUrl);

      const target = isThumbnail ? trimmedImageUrl : resolvedImageUrl;
      const retry = await resolveFileUrl(target);
      if (retry) {
        setImageSrcUrl(retry);
        return;
      }
    }
    setImageSrcUrl(
      src ?? fallbackSrc ?? require("../../assets/image/no-image.svg").default
    );
  };

  return imageUrl ? (
    <div
      className={`flex h-full w-full justify-center items-center relative ${
        containerClass ?? ""
      }`}
    >
      <img
        src={imageSrcUrl}
        alt={alt}
        className={`${
          isLoading && !noLoader ? "hidden" : "img-load-zoom-out"
        } ${className ?? ""}`}
        onLoad={handleImageLoad}
        {...rest}
        onError={handleImageError}
      />
      {isLoading && !noLoader && (
        <div
          className={`absolute top-0 left-0 h-full w-full flex justify-center items-center ${
            className ?? ""
          }`}
        >
          <div>
            <div className={`img-loader-test`} />
          </div>
        </div>
      )}
    </div>
  ) : (
    <div
      style={{ backgroundColor: fillColor }}
      className={`flex h-full w-full justify-center items-center relative ${
        containerClass ?? ""
      }`}
    >
      <img
        {...rest}
        alt={alt}
        className={`${className ?? ""} `}
        src={
          src ??
          fallbackSrc ??
          require("../../assets/image/no-image.svg").default
        }
      />
    </div>
  );
};

export default ICImage;
